import { useContext, useEffect, useState } from 'react'
import { FlagsClient, FlagsContext, FlagValue } from 'react-unleash-flags'
import { EFeatureFlags } from 'constants/featureFlags'

interface IFeatureFlagValue<T = boolean> {
  value: T
  isReady: boolean
  flagName: string
}

export default function useFeatureFlagValue<T = boolean>(
  name: EFeatureFlags,
  defaultValue: T
): IFeatureFlagValue<T> {
  const [value, setValue] = useState<T>(defaultValue)
  const [isReady, setIsReady] = useState<boolean>(false)
  const flagsClient: FlagsClient | undefined = useContext(FlagsContext)

  //* INFO: Flag name with environment
  const appName = import.meta.env.REACT_APP_FLAGS_CTX_APP_NAME || ''
  const prefixedFlagName: string = appName ? `${appName}-${name}` : `${name}`
  const flagName = prefixedFlagName

  useEffect(() => {
    try {
      if (!flagsClient) {
        setValue(defaultValue)
        setIsReady(false)
        return
      }

      const flag: FlagValue | undefined = flagsClient.getFlag(flagName)
      
      if (!flag) {
        setValue(defaultValue)
        setIsReady(false)
        return
      }

      // For boolean flags, use the enabled property
      if (typeof defaultValue === 'boolean') {
        setValue(flag.enabled as T)
        setIsReady(true)
        return
      }

      // For non-boolean flags, extract value from strategy parameters
      if (flag.enabled && flag.strategies && flag.strategies.length > 0) {
        const strategy = flag.strategies[0]
        if (strategy.parameters && strategy.parameters.value !== undefined) {
          setValue(strategy.parameters.value as T)
          setIsReady(true)
          return
        }
      }

      // Fallback to default value
      setValue(defaultValue)
      setIsReady(false)
    } catch (error) {
      console.warn(`Error getting feature flag ${flagName}:`, error)
      setValue(defaultValue)
      setIsReady(false)
    }
  }, [flagsClient, flagName, defaultValue])

  return { value, isReady, flagName }
}
